import type { I18N_CONF } from '@funkey/locales/config';

export namespace StoryblokUtils {
    export const LOCALES = ['default', 'nl', 'fr-be', 'en'] as const;
    export type Locale = (typeof LOCALES)[number];
    export namespace Locale {
        export function fromFunkeyLocale(locale: I18N_CONF.Locale): StoryblokUtils.Locale {
            switch (locale) {
                case 'nl-BE':
                    return 'default';
                case 'nl-NL':
                    return 'nl';
                case 'fr-BE':
                case 'fr-LU':
                    return 'fr-be';
                case 'en-BE':
                case 'en-NL':
                case 'en-LU':
                    return 'en';
                default:
                    locale satisfies never;
                    throw new Error('UNREACHABLE');
            }
        }

        export function toFunkeyLocales(sbLocale: StoryblokUtils.Locale): Array<I18N_CONF.Locale> {
            switch (sbLocale) {
                case 'default':
                    return ['nl-BE'];
                case 'nl':
                    return ['nl-NL'];
                case 'fr-be':
                    return ['fr-BE', 'fr-LU'];
                case 'en':
                    return ['en-BE', 'en-NL', 'en-LU'];
                default:
                    sbLocale satisfies never;
                    throw new Error('UNREACHABLE');
            }
        }
    }

    export namespace Story {
        type TranslatableStory = {
            name?: string;
            translated_slugs?: Array<{ lang: string; name: string | null }> | null;
        };

        /** A story's `name` is always the default-language one, whatever language the story was
         * fetched in. Only `translated_slugs[].name` carries the translation, so a menu built from
         * resolved stories reads Dutch on the French and English sites unless the translation is
         * looked up here. Falls back to the default name when an editor left the translated name
         * empty. */
        export function translatedName(
            story: TranslatableStory | undefined,
            locale: I18N_CONF.Locale,
        ): string | undefined {
            const sbLocale = Locale.fromFunkeyLocale(locale);

            return (
                story?.translated_slugs?.find(translation => translation.lang === sbLocale)?.name ||
                story?.name
            );
        }
    }
}
